import Tippy from '@tippyjs/react';
import type { Dispatch, FC, SetStateAction } from 'react';
import { memo, useCallback, useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import { Button } from '../../../../front/src/components/Button/Button';
import { ButtonV3 } from '../../../../front/src/components/Button/ButtonV3';
import { IconClose } from '../../../../front/src/components/icons/Icons';
import { isBrowser } from '../../../../front/src/utils/web';

export const messages = defineMessages({
  androidInstallPromptMsg: {
    defaultMessage: 'Installez cette application web sur votre smartphone pour plus de facilité.',
    id: 'QISeI7',
  },
  installBtnText: {
    defaultMessage: 'Installer',
    id: 'ByZ1z+',
  },
});

interface PromptEventInstance extends Event {
  userChoice: 'accepted' | 'dismissed';
  platform: string;
}

// BeforeInstallPromptEvent
interface PromptEvent extends Event {
  prompt: () => Promise<PromptEventInstance>;
  userChoice: 'accepted' | 'dismissed';
  platforms: string[];
}

// Initialize deferredPrompt for use later to show browser install prompt.
let deferredPrompt: PromptEvent | undefined = undefined;
let setPromptEvent: Dispatch<SetStateAction<PromptEvent | undefined>> | undefined = undefined;

if (isBrowser) {
  window.addEventListener('beforeinstallprompt', e => {
    // Prevent the mini-infobar from appearing on mobile
    // e.preventDefault();
    // Stash the event so it can be triggered later.
    const ev = e as PromptEvent;
    deferredPrompt = ev;
    // Update UI notify the user they can install the PWA
    setPromptEvent?.(ev);
    // Optionally, send analytics event that PWA install promo was shown.
  });
}

// Inspired from https://github.com/rajatsehgal/add-to-home-screen
export const AddToHomeScreenAndroid: FC = memo(function AddToHomeScreenAndroid(props) {
  const {} = props;

  const [visible, setVisible] = useState(true);
  const hide = useCallback(() => setVisible(false), []);

  const [promptEventFromState, setPromptEventFromState] = useState<PromptEvent>();
  if (isBrowser && !setPromptEvent) {
    // In case the React component renders before the event is broadcast.
    setPromptEvent = setPromptEventFromState;
  }

  const promptEvent = promptEventFromState || deferredPrompt;

  if (!promptEvent) return null;

  return (
    <div className="fixed z-10 bottom-5 left-1/2 -translate-x-1/2 flex w-full justify-center">
      <Tippy
        content={<AddToHomeScreenContent hide={hide} promptEvent={promptEvent} />}
        visible={visible}
        className="tippy-no-padding rounded-lg"
        maxWidth={450}
        theme="light-border"
        interactive
        arrow={false}
      >
        <div className="w-0 h-0"></div>
      </Tippy>
    </div>
  );
});

interface AddToHomeScreenContentProps {
  hide: VoidFunction;
  promptEvent: PromptEvent;
}

const AddToHomeScreenContent: FC<AddToHomeScreenContentProps> = memo(
  function AddToHomeScreenContent(props) {
    const { hide, promptEvent } = props;

    const handleInstall = useCallback(() => {
      if (isBrowser) {
        // deferredPrompt: if the event is broadcast before the component renders.
        promptEvent.prompt();
      }
    }, [promptEvent]);

    return (
      <div className="flex flex-col">
        <div className="self-end">
          <Button variant="icon" className="no-underline" onClick={hide}>
            <IconClose />
          </Button>
        </div>
        <div className="px-4 pb-4 leading-7">
          <FormattedMessage {...messages.androidInstallPromptMsg} />
        </div>
        <ButtonV3 variant="contained" onClick={handleInstall}>
          <FormattedMessage {...messages.installBtnText} />
        </ButtonV3>
      </div>
    );
  },
);
